import React, { useState, useEffect } from "react";
import { apiService } from "../../services/api";
import { PencilIcon } from "@heroicons/react/24/outline";
import EditTenantModal from "./EditTenantModal";
import { Tenant } from "../../types";

interface TenantMachineLimitCardProps {
  tenant: Tenant;
  onUpdate?: () => void;
}

export default function TenantMachineLimitCard({
  tenant,
  onUpdate,
}: TenantMachineLimitCardProps) {
  const [tenantStats, setTenantStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentTenant, setCurrentTenant] = useState<Tenant>(tenant);

  const loadTenantStats = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getTenantStatistics(tenant.id);
      setTenantStats(response);

      // Também atualizar o tenant atual com max_machines se disponível
      if (
        response &&
        (tenant as any).max_machines !== (response as any).max_machines
      ) {
        setCurrentTenant({
          ...tenant,
          max_machines: (response as any).max_machines,
        } as Tenant);
      }
    } catch (error) {
      console.error("Error loading tenant stats:", error);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    loadTenantStats();
  }, [loadTenantStats]);

  useEffect(() => {
    setCurrentTenant(tenant);
  }, [tenant]);

  const handleUpdateTenant = async (
    tenantId: string,
    data: any,
    files?: any
  ) => {
    try {
      // Atualizar dados básicos primeiro (incluindo max_machines)
      const updateData: any = {
        ...data,
      };
      if (data.max_machines !== undefined) {
        updateData.max_machines = data.max_machines;
      }

      await apiService.updateTenant(tenantId, updateData);

      // Se houver arquivos, fazer upload separadamente
      if (files) {
        if (files.logo) {
          await apiService.uploadTenantLogo(tenantId, files.logo);
        }
        if (files.background) {
          await apiService.uploadTenantBackground(tenantId, files.background);
        }
        if (files.contract) {
          await apiService.uploadTenantContract(
            tenantId,
            files.contract,
            files.contractExpiration
          );
        }
      }

      // Recarregar dados do tenant atualizado
      try {
        const updatedTenant = await apiService.getTenant(tenantId);
        if (updatedTenant.success && updatedTenant.data) {
          setCurrentTenant(updatedTenant.data);
        }
      } catch (err) {
        console.error("Error reloading tenant:", err);
        // Continuar mesmo se falhar
      }

      await loadTenantStats();
      if (onUpdate) {
        onUpdate();
      }
      setIsEditModalOpen(false);
    } catch (error: any) {
      console.error("Error updating tenant:", error);
      alert(error.response?.data?.message || "Erro ao atualizar empresa");
    }
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
    );
  }

  // Usar active_machines_count do tenant ou machines.total das stats
  // Priorizar active_machines_count do tenant, depois machines.total das stats
  const active_count =
    (currentTenant as any).active_machines_count ??
    tenantStats?.machines?.total ??
    0;
  const max_machines = (currentTenant as any).max_machines ?? null;
  const usage_percent =
    max_machines && max_machines > 0
      ? Math.round((active_count / max_machines) * 100 * 10) / 10
      : null;

  const isUnlimited = max_machines === null || max_machines === undefined;
  const remaining = isUnlimited ? null : max_machines - active_count;
  const isNearLimit =
    !isUnlimited && usage_percent !== null && usage_percent >= 80;
  const isAtLimit =
    !isUnlimited && usage_percent !== null && usage_percent >= 100;

  // Garantir que sempre renderiza, mesmo se não houver dados
  if (!tenantStats && !currentTenant) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Carregando informações de limite de máquinas...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Limite de Máquinas
          </h3>
          <button
            onClick={() => setIsEditModalOpen(true)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PencilIcon className="h-4 w-4 mr-1.5" />
            Editar
          </button>
        </div>

        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Máquinas Ativas
            </span>
            <span
              className={`text-2xl font-bold ${
                isAtLimit
                  ? "text-red-600 dark:text-red-400"
                  : isNearLimit
                  ? "text-yellow-600 dark:text-yellow-400"
                  : "text-gray-900 dark:text-white"
              }`}
            >
              {active_count}
            </span>
          </div>

          {!isUnlimited && (
            <>
              <div className="flex items-baseline justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Limite Máximo
                </span>
                <span className="text-lg font-semibold text-gray-900 dark:text-white">
                  {max_machines}
                </span>
              </div>

              {remaining !== null && (
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Disponíveis
                  </span>
                  <span
                    className={`text-lg font-semibold ${
                      remaining <= 0
                        ? "text-red-600 dark:text-red-400"
                        : remaining <= 5
                        ? "text-yellow-600 dark:text-yellow-400"
                        : "text-green-600 dark:text-green-400"
                    }`}
                  >
                    {remaining}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Sempre mostrar a barra de progresso */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Uso
              </span>
              {!isUnlimited && usage_percent !== null ? (
                <span
                  className={`text-xs font-medium ${
                    isAtLimit
                      ? "text-red-600 dark:text-red-400"
                      : isNearLimit
                      ? "text-yellow-600 dark:text-yellow-400"
                      : "text-gray-600 dark:text-gray-400"
                  }`}
                >
                  {usage_percent.toFixed(1)}%
                </span>
              ) : (
                <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Ilimitado
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                {!isUnlimited && usage_percent !== null ? (
                  <div
                    className={`h-2 rounded-full transition-all ${
                      isAtLimit
                        ? "bg-red-500 dark:bg-red-500"
                        : isNearLimit
                        ? "bg-yellow-500 dark:bg-yellow-500"
                        : "bg-green-500 dark:bg-green-500"
                    }`}
                    style={{ width: `${Math.min(usage_percent, 100)}%` }}
                  />
                ) : (
                  <div
                    className="h-2 rounded-full bg-green-500 dark:bg-green-500"
                    style={{ width: "100%" }}
                  />
                )}
              </div>
              {!isUnlimited && usage_percent !== null && (
                <span className="text-xs font-medium text-gray-900 dark:text-white">
                  {usage_percent.toFixed(1)}%
                </span>
              )}
            </div>
          </div>

          {isUnlimited && (
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400 italic">
              Sem limite de máquinas configurado
            </div>
          )}

          {isAtLimit && !isUnlimited && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
              <p className="text-sm text-red-800 dark:text-red-200">
                ⚠️ Limite de máquinas atingido. Não é possível adicionar mais
                máquinas.
              </p>
            </div>
          )}

          {isNearLimit && !isAtLimit && !isUnlimited && (
            <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                ⚠️ Limite de máquinas próximo. Restam apenas {remaining} máquina
                {remaining !== 1 ? "s" : ""}.
              </p>
            </div>
          )}
        </div>
      </div>

      {isEditModalOpen && (
        <EditTenantModal
          tenant={currentTenant}
          onSubmit={handleUpdateTenant}
          onCancel={() => setIsEditModalOpen(false)}
        />
      )}
    </>
  );
}
